import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileSignature, UserPlus, Copy, FileText, ShieldAlert, Archive, RotateCcw, Repeat } from "lucide-react";
import { employeeApi, employeeCleanupApi } from "../../services/employeeApi";
import { storeApi } from "../../services/storeApi";
import { onboardingApi } from "../../services/onboardingApi";
import { contractApi } from "../../services/contractApi";
import { noticeApi } from "../../services/noticeApi";
import { invitationApi } from "../../services/invitationApi";
import { medicalApi } from "../../services/medicalApi";
import { StatusBadge } from "../../components/StatusBadge";
import { OnboardingProgress } from "../../components/OnboardingProgress";
import { EagleState } from "../../components/EagleState";
import { ContractGenerateDialog } from "../../components/ContractGenerateDialog";
import { NoticeGenerateDialog } from "../../components/NoticeGenerateDialog";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { EmployeeAvatar } from "../../components/EmployeeAvatar";
import { AccessDenied } from "../../components/AccessDenied";
import { NotFoundState } from "../../components/NotFoundState";
import { StoreSelect } from "../../components/StoreSelect";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { AttendanceTab } from "../../components/employeeProfile/AttendanceTab";
import { PayrollTab } from "../../components/employeeProfile/PayrollTab";
import { LeaveTab } from "../../components/employeeProfile/LeaveTab";
import { TrainingTab } from "../../components/employeeProfile/TrainingTab";
import { MonthlyRecordTab } from "../../components/employeeProfile/MonthlyRecordTab";
import { PeriodSelector, currentPeriod } from "../../components/employeeProfile/monthlyRecord/PeriodSelector";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";
import { formatApiErrorDetail } from "../../lib/api";
import { WORK_STATUSES, ROLES } from "../../lib/constants";
import { useLocalization } from "../../hooks/useLocalization";
import { usePermissions } from "../../hooks/usePermissions";

export default function EmployeeProfilePage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLocalization();
  const perm = usePermissions();
  const { canManageOnboarding, canCreateEmployeeInvitation, canManageNotices, canManageMedicalChecks, canViewPayroll, canViewLeave, canViewTraining } = perm;
  const [employee, setEmployee] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [onboardingCase, setOnboardingCase] = useState(null);
  const [contract, setContract] = useState(null);
  const [notice, setNotice] = useState(null);
  const [medicalCheck, setMedicalCheck] = useState(null);
  const [editingFrequency, setEditingFrequency] = useState(false);
  const [frequencyValue, setFrequencyValue] = useState("12");
  const [frequencyReason, setFrequencyReason] = useState("");
  const [acknowledging, setAcknowledging] = useState(false);
  const [completedDate, setCompletedDate] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showNoticeDialog, setShowNoticeDialog] = useState(false);
  const [statusReason, setStatusReason] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");
  const [grantedLink, setGrantedLink] = useState("");
  const [grantedInvitationId, setGrantedInvitationId] = useState("");
  const [granting, setGranting] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [cleanupConfirmText, setCleanupConfirmText] = useState("");
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferStoreName, setTransferStoreName] = useState(null);
  const [transferStoreId, setTransferStoreId] = useState(null);
  const [transferEffectiveDate, setTransferEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [transferReason, setTransferReason] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [transferPreview, setTransferPreview] = useState(null);
  const [stores, setStores] = useState([]);
  const [pendingNoLongerWorkingStatus, setPendingNoLongerWorkingStatus] = useState(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const activeTab = searchParams.get("tab") || "overview";
  const period = searchParams.get("period") || currentPeriod();

  const setActiveTab = (tab) => setSearchParams({ tab, period });
  const setPeriod = (newPeriod) => setSearchParams({ tab: activeTab, period: newPeriod });

  useEffect(() => {
    if (!searchParams.get("period")) setSearchParams({ tab: activeTab, period }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(() => {
    employeeApi.get(employeeId)
      .then((r) => { setEmployee(r.data); setAccessDenied(false); setNotFound(false); })
      .catch((err) => {
        if (err.response?.status === 403) setAccessDenied(true);
        else if (err.response?.status === 404) setNotFound(true);
      });
    onboardingApi.getCaseByEmployee(employeeId).then((r) => setOnboardingCase(r.data)).catch(() => {});
    contractApi.getByEmployee(employeeId).then((r) => setContract(r.data)).catch(() => setContract(null));
    noticeApi.getCurrentByEmployee(employeeId).then((r) => setNotice(r.data)).catch(() => setNotice(null));
    if (canManageMedicalChecks) medicalApi.getCheck(employeeId).then((r) => setMedicalCheck(r.data)).catch(() => setMedicalCheck(null));
  }, [employeeId, canManageMedicalChecks]);

  useEffect(() => { load(); }, [load]);
  useRouteRefresh(load);

  if (accessDenied) return <AccessDenied resourceLabel={t("employees.title")} />;
  if (notFound) return <NotFoundState testId="employee-not-found" backTo="/management/employees" backLabel={t("invitations.backToDirectory")} />;
  if (!employee) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  const clearanceGranted = employee.work_clearance_status === "CLEARED" || employee.work_clearance_status === "OVERRIDDEN";

  const handleWorkStatusChange = async (status) => {
    if (status === employee.work_status) return;
    if (status === "NO_LONGER_WORKING") { setPendingNoLongerWorkingStatus(status); return; }
    await applyWorkStatusChange(status);
  };

  const applyWorkStatusChange = async (status) => {
    setPendingStatus(status);
    try {
      const { data } = await employeeApi.updateWorkStatus(employeeId, status, statusReason || undefined);
      setEmployee(data);
      setStatusReason("");
      toast.success(t("workStatus.updateSuccess"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPendingStatus("");
    }
  };

  const handleGrantStaffAccess = async () => {
    setGranting(true);
    try {
      const { data } = await invitationApi.create({
        email: employee.personal_email, name: employee.legal_name, role: ROLES.EMPLOYEE, employee_id: employee.id,
      });
      setGrantedLink(data.activation_link_preview);
      setGrantedInvitationId(data.id);
      toast.success(t("workStatus.staffAccessGranted"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGranting(false);
    }
  };

  const saveFrequency = async () => {
    try {
      const { data } = await medicalApi.setFrequency(employeeId, { frequency_months: Number(frequencyValue), reason: frequencyReason || "Management override" });
      setMedicalCheck(data);
      setEditingFrequency(false); setFrequencyReason("");
      toast.success("Frequency updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveAcknowledgement = async () => {
    if (!completedDate) { toast.error("Enter the completion date"); return; }
    try {
      const { data } = await medicalApi.acknowledgeCompleted(employeeId, { completed_date: completedDate });
      setMedicalCheck(data);
      setAcknowledging(false); setCompletedDate("");
      toast.success("Medical check acknowledged - next renewal date set");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleArchive = async () => {
    try {
      const { data } = await employeeApi.archive(employeeId);
      setEmployee(data);
      toast.success(t("employees.archiveSuccess"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleReactivate = async () => {
    try {
      const { data } = await employeeApi.reactivate(employeeId);
      setEmployee(data);
      toast.success(t("employees.reactivateSuccess"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openCleanupDialog = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await employeeCleanupApi.previewOne(employeeId);
      setCleanupPreview(data);
      setCleanupConfirmText("");
      setShowCleanupDialog(true);
    } finally {
      setCleanupBusy(false);
    }
  };

  const executeCleanup = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await employeeCleanupApi.execute([employeeId], "delete", cleanupConfirmText);
      if (data.deleted.length > 0) {
        toast.success(t("testDataReview.batchComplete", { deleted: 1, archived: 0, blocked: 0 }));
        navigate("/management/employees");
      } else {
        toast.error(data.blocked?.[0]?.reason || "Cleanup blocked");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup action failed");
    } finally {
      setCleanupBusy(false);
      setShowCleanupDialog(false);
    }
  };

  const openTransferDialog = async () => {
    setTransferStoreName(null);
    setTransferStoreId(null);
    setTransferEffectiveDate(new Date().toISOString().slice(0, 10));
    setTransferReason("");
    setTransferError("");
    setTransferPreview(null);
    setShowTransferDialog(true);
    storeApi.list(true).then((res) => setStores(res.data)).catch(() => {});
  };

  const handleTransferStoreChange = async (name) => {
    setTransferStoreName(name);
    setTransferPreview(null);
    const match = stores.find((s) => s.name === name);
    const storeId = match ? match.id : null;
    setTransferStoreId(storeId);
    if (storeId) {
      try {
        const { data } = await employeeApi.getTransferPreview(employeeId, storeId);
        setTransferPreview(data);
      } catch { /* preview is informational only - a failed fetch should not block the transfer itself */ }
    }
  };

  const submitTransfer = async () => {
    setTransferError("");
    if (!transferStoreId) { setTransferError("Select a destination store."); return; }
    if (!transferReason.trim()) { setTransferError("A transfer reason is required for the audit record."); return; }
    setTransferBusy(true);
    try {
      const { data } = await employeeApi.transferStore(employeeId, transferStoreId, transferReason, transferEffectiveDate);
      setEmployee(data);
      toast.success(`Store transfer complete - now assigned to ${transferStoreName}`);
      setShowTransferDialog(false);
    } catch (err) {
      setTransferError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTransferBusy(false);
    }
  };

  return (
    <div className="max-w-6xl" data-testid="employee-profile-page">
      <PageHelpBanner pageKey="employee-profile" title="How to use this page">
        <p>This is the single record for one employee. Use the tabs below to move between their employment status, attendance history, payroll, leave, training, and compliance records - everything about this person lives here.</p>
        <p><strong>Tip:</strong> Use the month/year picker above the tabs to pick any period, then open <strong>Monthly Record</strong> for a full cross-domain snapshot of that month (schedule, attendance, reconciliation, payroll, leave, and more) - it also drives the Attendance tab below.</p>
      </PageHelpBanner>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-4">
          <EmployeeAvatar employee={employee} editable={canManageOnboarding} size={64} onUpdated={load} />
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">{employee.legal_name}</h2>
            <p className="text-sm text-gray-500">{employee.employee_id} · {employee.position} · {employee.store_location}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {employee.employment_status === "ARCHIVED" ? (
            perm.canReactivateEmployee && (
              <button data-testid="reactivate-employee-button" onClick={handleReactivate} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-300 text-emerald-700 text-xs font-semibold hover:bg-emerald-50">
                <RotateCcw size={13} /> {t("employees.reactivateEmployee")}
              </button>
            )
          ) : (
            perm.canArchiveEmployee && (
              <button data-testid="archive-employee-button" onClick={() => setShowArchiveDialog(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 text-xs font-semibold hover:bg-gray-50">
                <Archive size={13} /> {t("employees.archiveEmployee")}
              </button>
            )
          )}
          {perm.canTransferEmployeeStore && (
            <button data-testid="transfer-store-button" onClick={openTransferDialog} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-blue-200 text-blue-600 text-xs font-semibold hover:bg-blue-50">
              <Repeat size={13} /> Transfer Store
            </button>
          )}
          {perm.canPreviewTestCleanup && (
            <button data-testid="clean-up-test-record-button" disabled={cleanupBusy} onClick={openCleanupDialog} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 disabled:opacity-50">
              <ShieldAlert size={13} /> {t("employees.cleanUpTestRecord")}
            </button>
          )}
          <StatusBadge status={employee.work_clearance_status} />
        </div>
      </div>

      {clearanceGranted && (
        <div className="bg-white rounded-xl border border-emerald-200 mb-6">
          <EagleState variant="success" title={t("eagle.workClearanceGrantedTitle")} message={t("eagle.workClearanceGrantedMessage")} testId="employee-clearance-granted" />
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="work-status-panel">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("workStatus.title")}</p>
          <StatusBadge status={employee.work_status || "ACTIVE"} />
        </div>
        {canManageOnboarding && (
          <div className="flex flex-wrap items-center gap-2">
            {WORK_STATUSES.map((status) => (
              <button
                key={status}
                data-testid={`work-status-set-${status}`}
                disabled={pendingStatus !== "" || status === employee.work_status}
                onClick={() => handleWorkStatusChange(status)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all active:scale-95 disabled:opacity-40 ${
                  status === employee.work_status ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {t(`statuses.${status}`)}
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">
          {employee.work_status === "NO_LONGER_WORKING" ? t("workStatus.noLongerWorkingWarning") : employee.work_status === "INACTIVE" ? t("workStatus.inactiveNote") : t("workStatus.activeNote")}
        </p>

        <div className="border-t border-gray-100 mt-4 pt-4">
          {employee.user_id ? (
            <p className="text-xs font-semibold text-emerald-600 flex items-center gap-1.5" data-testid="staff-access-granted-label">
              <UserPlus size={13} /> {t("workStatus.hasStaffAccess")}
            </p>
          ) : canCreateEmployeeInvitation ? (
            <button
              data-testid="grant-staff-access-button"
              disabled={granting}
              onClick={handleGrantStaffAccess}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
            >
              <UserPlus size={14} /> {t("workStatus.grantStaffAccess")}
            </button>
          ) : (
            <p className="text-xs text-gray-500">{t("workStatus.noStaffAccess")}</p>
          )}
          {grantedLink && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-3 flex items-center justify-between gap-3" data-testid="staff-access-link-preview">
              <p className="text-xs text-amber-900 truncate font-mono">{grantedLink}</p>
              <button
                data-testid="copy-staff-access-link-button"
                onClick={() => { navigator.clipboard.writeText(grantedLink); if (grantedInvitationId) invitationApi.markCopied(grantedInvitationId).catch(() => {}); toast.success(t("invitations.linkCopied")); }}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 active:scale-95 transition-all shrink-0"
              >
                <Copy size={12} /> {t("invitations.copyLink")}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-6" data-testid="employee-profile-period-selector-wrapper">
        <PeriodSelector period={period} onChange={setPeriod} />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex-wrap h-auto justify-start" data-testid="employee-profile-tabs">
          <TabsTrigger value="overview" data-testid="tab-trigger-overview">Overview</TabsTrigger>
          <TabsTrigger value="monthly-record" data-testid="tab-trigger-monthly-record">Monthly Record</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-trigger-attendance">Attendance</TabsTrigger>
          {canViewPayroll && <TabsTrigger value="payroll" data-testid="tab-trigger-payroll">Payroll</TabsTrigger>}
          {canViewLeave && <TabsTrigger value="leave" data-testid="tab-trigger-leave">Leave</TabsTrigger>}
          {canViewTraining && <TabsTrigger value="training" data-testid="tab-trigger-training">Training</TabsTrigger>}
          {canManageMedicalChecks && <TabsTrigger value="compliance" data-testid="tab-trigger-compliance">Health & Compliance</TabsTrigger>}
          <TabsTrigger value="documents" data-testid="tab-trigger-documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Profile</p>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between"><dt className="text-gray-500">{t("employees.personalEmail")}</dt><dd>{employee.personal_email}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">{t("employees.employmentType")}</dt><dd>{employee.employment_type}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">{t("employees.department")}</dt><dd>{employee.department || "-"}</dd></div>
              </dl>
            </div>
            {onboardingCase && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">{t("onboarding.title")}</p>
                <OnboardingProgress requirements={onboardingCase.requirements} />
                <Link
                  to={`/management/onboarding/${onboardingCase.id}`}
                  data-testid="view-onboarding-case-link"
                  className="inline-block mt-4 text-sm font-semibold text-blue-600 hover:underline"
                >
                  {t("onboarding.title")} →
                </Link>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="monthly-record" className="mt-4">
          <MonthlyRecordTab
            employeeId={employeeId} period={period} employee={employee}
            contract={contract} notice={notice} medicalCheck={medicalCheck} perm={perm}
          />
        </TabsContent>

        <TabsContent value="attendance" className="mt-4">
          <AttendanceTab employeeId={employeeId} period={period} />
        </TabsContent>

        {canViewPayroll && (
          <TabsContent value="payroll" className="mt-4">
            <PayrollTab employeeId={employeeId} />
          </TabsContent>
        )}

        {canViewLeave && (
          <TabsContent value="leave" className="mt-4">
            <LeaveTab employeeId={employeeId} />
          </TabsContent>
        )}

        {canViewTraining && (
          <TabsContent value="training" className="mt-4">
            <TrainingTab employeeId={employeeId} />
          </TabsContent>
        )}

        {canManageMedicalChecks && (
          <TabsContent value="compliance" className="mt-4">
            {medicalCheck ? (
              <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="employee-medical-check-panel">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Medical Check</p>
                  <StatusBadge status={medicalCheck.status} />
                </div>
                <dl className="text-sm space-y-2 mb-3">
                  <div className="flex justify-between"><dt className="text-gray-500">Frequency</dt>
                    <dd>
                      {editingFrequency ? (
                        <span className="inline-flex items-center gap-1">
                          <input data-testid="profile-frequency-months-input" value={frequencyValue} onChange={(e) => setFrequencyValue(e.target.value)} className="border border-gray-200 rounded px-2 py-1 text-xs w-14" />
                          <input data-testid="profile-frequency-reason-input" value={frequencyReason} onChange={(e) => setFrequencyReason(e.target.value)} placeholder="reason" className="border border-gray-200 rounded px-2 py-1 text-xs w-32" />
                          <button data-testid="profile-frequency-save-btn" onClick={saveFrequency} className="px-2 py-1 rounded text-xs font-semibold bg-blue-600 text-white">Save</button>
                        </span>
                      ) : (
                        <button data-testid="profile-frequency-edit-btn" onClick={() => { setEditingFrequency(true); setFrequencyValue(String(medicalCheck.frequency_months)); }} className="hover:underline">{medicalCheck.frequency_months} months</button>
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between"><dt className="text-gray-500">Last completed</dt><dd data-testid="profile-medical-last-completed">{medicalCheck.last_completed_date || "-"}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Next due (drop-dead date)</dt><dd data-testid="profile-medical-next-due">{medicalCheck.next_due_date || "Not yet assigned"}</dd></div>
                  <div className="flex justify-between"><dt className="text-gray-500">Scheduled appointment</dt><dd>{medicalCheck.scheduled_appointment_date || "None"}</dd></div>
                </dl>

                {acknowledging ? (
                  <span className="inline-flex items-center gap-2">
                    <input data-testid="profile-completed-date-input" type="date" value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
                    <button data-testid="profile-acknowledge-save-btn" onClick={saveAcknowledgement} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700">Save</button>
                  </span>
                ) : (
                  <button data-testid="profile-acknowledge-completed-btn" onClick={() => setAcknowledging(true)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">Acknowledge Completed (new exam report received)</button>
                )}

                {medicalCheck.completion_history?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">History (physical reports filed in office binder)</p>
                    <ul className="text-xs text-gray-500 space-y-1" data-testid="medical-history-list">
                      {medicalCheck.completion_history.slice().reverse().map((h, i) => (
                        <li key={i}>{h.completed_date} - acknowledged {new Date(h.acknowledged_at).toLocaleDateString()}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <EagleState variant="empty" title="No medical check record" message="A medical check schedule has not been created for this employee yet." testId="no-medical-check-empty" />
            )}
          </TabsContent>
        )}

        <TabsContent value="documents" className="mt-4 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("contracts.title")}</p>
              {canManageOnboarding && (!contract || contract.status === "REPLACED" || contract.status === "DECLINED" || contract.status === "CANCELLED") && (
                <button
                  data-testid="generate-contract-button"
                  onClick={() => setShowDialog(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 active:scale-95 transition-all"
                >
                  <FileSignature size={14} /> {t("contractGen.generateContract")}
                </button>
              )}
            </div>

            {contract ? (
              contract.status === "FULLY_EXECUTED" ? (
                <EagleState variant="success" title={t("eagle.contractExecutedTitle")} message={t("eagle.contractExecutedMessage")} testId="contract-fully-executed" />
              ) : (
                <div className="flex items-center justify-between" data-testid="contract-summary">
                  <p className="text-sm text-gray-700">Contract v{contract.version}</p>
                  <StatusBadge status={contract.status} />
                </div>
              )
            ) : (
              <EagleState variant="empty" title={t("eagle.noContractsTitle")} message={t("eagle.noContractsMessage")} testId="no-contract-empty" />
            )}
          </div>

          {showDialog && (
            <ContractGenerateDialog
              employee={employee}
              onClose={() => setShowDialog(false)}
              onGenerated={() => { setShowDialog(false); load(); }}
            />
          )}

          <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="notice-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("notice.title")}</p>
              {canManageNotices && (!notice || notice.status === "REPLACED") && (
                <button
                  data-testid="generate-notice-button"
                  onClick={() => setShowNoticeDialog(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 active:scale-95 transition-all"
                >
                  <FileText size={14} /> {t("notice.generate")}
                </button>
              )}
            </div>
            {notice ? (
              <Link to={`/management/notices/${notice.id}`} data-testid="notice-summary-link" className="flex items-center justify-between hover:underline">
                <p className="text-sm text-gray-700">{t("notice.version")} {notice.version}</p>
                <StatusBadge status={notice.status} />
              </Link>
            ) : (
              <EagleState variant="empty" title={t("notice.noNotice")} message={t("notice.subtitle")} testId="no-notice-empty" />
            )}
          </div>

          {showNoticeDialog && (
            <NoticeGenerateDialog
              employee={employee}
              onClose={() => setShowNoticeDialog(false)}
              onGenerated={(data) => { setShowNoticeDialog(false); navigate(`/management/notices/${data.id}`); }}
            />
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={showCleanupDialog} onOpenChange={(open) => !open && setShowCleanupDialog(false)}>
        <DialogContent className="max-w-lg" data-testid="profile-cleanup-preview-dialog">
          <DialogHeader><DialogTitle>{t("employees.cleanUpTestRecord")}</DialogTitle></DialogHeader>
          {cleanupPreview && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">{cleanupPreview.employee_name} ({cleanupPreview.employee_code})</p>
                <StatusBadge status={cleanupPreview.classification} />
              </div>
              <p className="text-xs text-gray-500" data-testid="profile-cleanup-dependency-count">
                {t("testDataReview.dependencyMap")}: {cleanupPreview.dependency_map.total_linked_records} record(s)
              </p>
              {cleanupPreview.eligible_for_hard_delete ? (
                <p className="text-xs text-emerald-600" data-testid="profile-cleanup-eligible">
                  {t("testDataReview.eligibleForDelete")} - {t("testDataReview.noProtectedHistory")}
                </p>
              ) : (
                <p className="text-xs text-red-600" data-testid="profile-cleanup-blocked">
                  {t("testDataReview.notEligibleForDelete")}: {cleanupPreview.block_reason}
                </p>
              )}
              {cleanupPreview.eligible_for_hard_delete && (
                <div className="space-y-2 pt-2 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500">{t("testDataReview.typeToConfirm", { text: "DELETE TEST RECORD" })}</p>
                  <input
                    data-testid="profile-cleanup-confirm-text-input"
                    value={cleanupConfirmText}
                    onChange={(e) => setCleanupConfirmText(e.target.value)}
                    placeholder={t("testDataReview.confirmationPlaceholder")}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button data-testid="profile-cleanup-cancel-btn" onClick={() => setShowCleanupDialog(false)} className="px-4 py-2 rounded-full text-sm font-semibold border border-gray-300">
              {t("testDataReview.cancel")}
            </button>
            {cleanupPreview?.eligible_for_hard_delete && (
              <button
                data-testid="profile-cleanup-confirm-execute-btn"
                disabled={cleanupBusy || cleanupConfirmText !== "DELETE TEST RECORD"}
                onClick={executeCleanup}
                className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40"
              >
                {t("testDataReview.confirmDelete")}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTransferDialog} onOpenChange={(open) => !open && setShowTransferDialog(false)}>
        <DialogContent className="max-w-lg" data-testid="transfer-store-dialog">
          <DialogHeader><DialogTitle>Transfer Store - {employee.legal_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Current store: <span className="font-semibold text-gray-700">{employee.store_location || "(none)"}</span></p>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Destination Store</label>
              <StoreSelect testId="transfer-store-destination-select" value={transferStoreName} onChange={handleTransferStoreChange} includeInactive />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Effective Date</label>
              <input data-testid="transfer-effective-date-input" type="date" value={transferEffectiveDate} onChange={(e) => setTransferEffectiveDate(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Transfer Reason (required, for audit)</label>
              <textarea data-testid="transfer-reason-input" value={transferReason} onChange={(e) => setTransferReason(e.target.value)} rows={2} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
            </div>

            {transferPreview && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5" data-testid="transfer-preview-panel">
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Before you confirm - review what stays tied to {transferPreview.current_store?.name || "the current store"}</p>
                <p className="text-xs text-amber-800" data-testid="transfer-preview-future-schedules">Future schedules at the current store: {transferPreview.future_schedule_count}</p>
                <p className="text-xs text-amber-800" data-testid="transfer-preview-open-timecards">Open timecards not yet locked for payroll: {transferPreview.open_timecard_count}</p>
                <p className="text-xs text-amber-800" data-testid="transfer-preview-payroll-period">Current payroll period in progress: {transferPreview.current_payroll_period_in_progress ? "Yes" : "No"}</p>
                <p className="text-xs text-amber-800" data-testid="transfer-preview-pending-leave">Pending leave requests: {transferPreview.pending_leave_count}</p>
                <p className="text-[11px] text-amber-700 pt-1">Past schedules, timecards, and payroll lines are never rewritten - only new records from the effective date use the destination store. Remember to also review this employee's manager/store scope after the transfer.</p>
                {transferPreview.new_store?.status !== "ACTIVE" && (
                  <p className="text-xs font-semibold text-red-600" data-testid="transfer-preview-inactive-warning">This destination store is inactive and cannot receive a transfer.</p>
                )}
              </div>
            )}

            {transferError && <p className="text-sm text-red-500" data-testid="transfer-store-error">{transferError}</p>}
          </div>
          <DialogFooter>
            <button data-testid="transfer-store-cancel-btn" onClick={() => setShowTransferDialog(false)} className="px-4 py-2 rounded-full text-sm font-semibold border border-gray-300">Cancel</button>
            <button data-testid="transfer-store-confirm-btn" onClick={submitTransfer} disabled={transferBusy || !transferStoreId || transferPreview?.new_store?.status !== "ACTIVE"} className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40">
              {transferBusy ? "Transferring..." : "Confirm Transfer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingNoLongerWorkingStatus} onOpenChange={(v) => !v && setPendingNoLongerWorkingStatus(null)}
        title={t("workStatus.noLongerWorkingWarning")} danger confirmLabel={t(`statuses.NO_LONGER_WORKING`)}
        onConfirm={() => applyWorkStatusChange(pendingNoLongerWorkingStatus)} testId="no-longer-working-confirm-dialog"
      />
      <ConfirmDialog
        open={showArchiveDialog} onOpenChange={setShowArchiveDialog}
        title={t("employees.confirmArchive")} danger confirmLabel={t("employees.archiveEmployee")}
        onConfirm={handleArchive} testId="archive-employee-dialog"
      />
    </div>
  );
}
